import { useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AdminPanelSettingsOutlined, Close } from "@mui/icons-material";
import FormRol from "./FormRol";

export default function RoleDialog({ open, onClose, onSubmit, initialData }) {
  const titleId = useId();
  const lastData = useRef(initialData);
  if (open) lastData.current = initialData;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const close = () => {
    if (!savingRef.current) onClose();
  };
  const submit = async (values) => {
    savingRef.current = true;
    setSaving(true);
    try {
      await onSubmit(values);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      disableEscapeKeyDown={saving}
      PaperProps={{
        sx: {
          height: { xs: "100%", sm: "min(850px, 92dvh)" },
          maxHeight: { xs: "100%", sm: "92dvh" },
          borderRadius: { xs: 0, sm: 3 },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        component="div"
        id={`${titleId}-header`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, sm: 3 },
          py: 2,
          background: "linear-gradient(100deg, #1a73e8, #49a3f1)",
          color: "#fff",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.16)",
            flexShrink: 0,
          }}
        >
          <AdminPanelSettingsOutlined sx={{ color: "#fff", fontSize: 26 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography id={titleId} variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
            {lastData.current ? "Editar rol y permisos" : "Crear rol"}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.25 }}>
            {lastData.current?.name || "Configure los accesos por módulo."}
          </Typography>
        </Box>
        {lastData.current?.id != null && (
          <Chip
            label={`ID ${lastData.current.id}`}
            size="small"
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              bgcolor: "rgba(255,255,255,0.16)",
              color: "#fff",
              fontWeight: 600,
            }}
          />
        )}
        <IconButton
          aria-label="Cerrar editor de rol"
          onClick={close}
          disabled={saving}
          sx={{ color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      {open && (
        <FormRol
          initialData={initialData}
          onSubmit={submit}
          onCancel={close}
          isEditing={Boolean(initialData)}
        />
      )}
    </Dialog>
  );
}
RoleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
};
RoleDialog.defaultProps = { initialData: null };
